import { Button } from "../../ui/components/Button";
import { Dialog, DialogTrigger } from "../../ui/components/Dialog";
import { Tooltip, TooltipTrigger } from "../../ui/components/Tooltip";
import { History, Redo, Undo } from "lucide-react";
import { Modal } from "react-aria-components";
import { useState, useEffect } from "react";
import { luminaService } from "../services/luminaService";
import { Client, VERSION } from "@luminaphoto/lumina-js";

// Use the HistoryManager class and infer the entry type from its methods
type HistoryEntry = ReturnType<Client.HistoryManager["getAllEntries"]>[0];
import { ThemeToggle } from "../../theme";

interface EditorHeaderProps {
  hasImage: boolean;
  onPreviewUpdate?: (url: string) => void;
}

export function EditorHeader({ hasImage, onPreviewUpdate }: EditorHeaderProps) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);

  // Update history state when operations change
  useEffect(() => {
    const updateHistoryState = () => {
      if (!luminaService.initialized || !luminaService.imageLoaded) {
        setCanUndo(false);
        setCanRedo(false);
        setHistoryEntries([]);
        return;
      }

      setCanUndo(luminaService.canUndo());
      setCanRedo(luminaService.canRedo());
      setHistoryEntries(luminaService.getAllHistoryEntries());
      setCurrentHistoryIndex(luminaService.getCurrentHistoryIndex());
    };

    // Check immediately
    updateHistoryState();

    // Set up interval to check periodically (since we don't have direct event subscription)
    const interval = setInterval(updateHistoryState, 100);
    return () => clearInterval(interval);
  }, [hasImage]);

  const handleUndo = async () => {
    try {
      const previewUrl = await luminaService.undo();
      onPreviewUpdate?.(previewUrl);
    } catch (error) {
      console.error("Undo failed:", error);
    }
  };

  const handleRedo = async () => {
    try {
      const previewUrl = await luminaService.redo();
      onPreviewUpdate?.(previewUrl);
    } catch (error) {
      console.error("Redo failed:", error);
    }
  };

  const formatOperationType = (entry: HistoryEntry): string => {
    // Remove "apply" prefix and format the operation name
    const name = entry.operationType
      .replace(/^apply/i, "")
      .replace(/([A-Z])/g, " $1")
      .trim();
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const formatPayload = (entry: HistoryEntry): string => {
    const payload = entry.payload;
    const params: string[] = [];

    // Just format whatever the SDK recorded
    Object.entries(payload).forEach(([key, value]) => {
      if (typeof value === "number") {
        params.push(`${key}: ${value.toFixed(2)}`);
      } else if (Array.isArray(value)) {
        // Format array of points with coordinates
        const pointsStr = value
          .map((point) =>
            typeof point === "object" && point !== null && "x" in point && "y" in point
              ? `(${(point as { x: number; y: number }).x.toFixed(2)}, ${(point as { x: number; y: number }).y.toFixed(2)})`
              : String(point)
          )
          .join(", ");
        params.push(`${key}: ${pointsStr}`);
      } else if (typeof value === "object" && value !== null) {
        // Count non-zero numeric properties for objects (like color grading)
        const nonZeroProps = Object.values(value).filter(
          (v) => typeof v === "number" && v !== 0
        ).length;
        if (nonZeroProps > 0) {
          params.push(`${key}: ${nonZeroProps} changes`);
        }
      }
    });

    return params.length > 0 ? params.join(", ") : "Applied";
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className="bg-background border-b px-6 py-1">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-[10px]">
          <a
            href="https://luminaphoto.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[17px] font-normal tracking-[0.01em] text-purple-gradient"
          >
            Lumina JS
          </a>
          <span className="font-mono text-[10px] font-normal tracking-[0.04em] text-muted-foreground border border-border rounded-[3px] px-[7px] py-[2px] leading-[1.5]">
            v{VERSION}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {hasImage && (
            <>
              {/* Undo Button */}
              <TooltipTrigger delay={300}>
                <Button variant="ghost" size="sm" onPress={handleUndo} isDisabled={!canUndo}>
                  <Undo className="h-4 w-4" />
                </Button>
                <Tooltip>Undo</Tooltip>
              </TooltipTrigger>

              {/* Redo Button */}
              <TooltipTrigger delay={300}>
                <Button variant="ghost" size="sm" onPress={handleRedo} isDisabled={!canRedo}>
                  <Redo className="h-4 w-4" />
                </Button>
                <Tooltip>Redo</Tooltip>
              </TooltipTrigger>

              {/* History Dialog */}
              <DialogTrigger>
                <TooltipTrigger delay={300}>
                  <Button variant="ghost" size="sm">
                    <History className="h-4 w-4" />
                  </Button>
                  <Tooltip>View History</Tooltip>
                </TooltipTrigger>
                <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                  <Dialog className="rounded-lg bg-background p-6 shadow-lg max-w-md w-full mx-4 md:mx-0">
                    {({ close }) => (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg">Edit History</h3>
                          <Button variant="ghost" size="sm" onPress={close}>
                            <span className="sr-only">Close</span>✕
                          </Button>
                        </div>

                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {historyEntries.length === 0 ? (
                            <div className="text-muted-foreground text-center py-8">
                              No edits yet. Start making changes to see history.
                            </div>
                          ) : (
                            historyEntries.map((entry, index) => {
                              const isActive = index <= currentHistoryIndex;
                              const isCurrent = index === currentHistoryIndex;
                              return (
                                <div
                                  key={entry.id}
                                  className={`border rounded p-3 space-y-1 ${
                                    isActive
                                      ? isCurrent
                                        ? "border-primary bg-primary/5"
                                        : "border-border bg-background"
                                      : "border-muted bg-muted/30 opacity-50"
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div
                                      className={`font-medium text-sm flex items-center gap-2 ${
                                        isActive ? "text-foreground" : "text-muted-foreground"
                                      }`}
                                    >
                                      {isCurrent && (
                                        <div className="w-2 h-2 bg-primary rounded-full" />
                                      )}
                                      {formatOperationType(entry)}
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                      {formatTimestamp(entry.timestamp)}
                                    </div>
                                  </div>
                                  <div
                                    className={`text-xs ${
                                      isActive
                                        ? "text-muted-foreground"
                                        : "text-muted-foreground/50"
                                    }`}
                                  >
                                    {formatPayload(entry)}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        <div className="flex justify-end">
                          <Button onPress={close}>Close</Button>
                        </div>
                      </div>
                    )}
                  </Dialog>
                </Modal>
              </DialogTrigger>

              <div className="w-px h-6 bg-border mx-2" />
            </>
          )}

          <a
            href="https://luminaphoto.io/api/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground px-3 h-8 text-muted-foreground"
          >
            Documentation
          </a>

          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
